import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useModal } from '../../context/ModalContext';
import { hkDateISO, hkDateISOPlusDays } from '../../utils/dateUtils';
import { isValidEmail, findInvalidEmails } from '../../utils/emailUtils';
import { copyTextToClipboard } from '../../utils/clipboard.js';

export default function EditorLinkModal({ show, formId, onClose }) {
  const [patientName, setPatientName] = useState('');
  const [notes, setNotes] = useState('');
  // Hong Kong calendar day + 3. toISOString() would give the UTC day, which is
  // yesterday for any time before 08:00 HKT.
  const [expirationDate, setExpirationDate] = useState(() => hkDateISOPlusDays(3));
  const [neverExpires, setNeverExpires] = useState(true);
  const [reusable, setReusable] = useState(true);
  const [doctorEmail, setDoctorEmail] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [bccEmail, setBccEmail] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [linkResult, setLinkResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [customHost, setCustomHost] = useState('');

  useEffect(() => {
    if (show) {
      setPatientName('');
      setNotes('');
      setExpirationDate(hkDateISOPlusDays(3));
      setNeverExpires(true);
      setReusable(true);
      setDoctorEmail('');
      setCcEmail('');
      setBccEmail('');
      setLinkResult(null);
      setCustomHost('');
      // Load doctors
      api.get('/doctors').then(res => setDoctors(res.data || [])).catch(() => setDoctors([]));
    }
  }, [show]);

  const { showAlert } = useModal();

  if (!show) return null;

  const handleCreateLink = async () => {
    // Recipient is optional here, so only validate a value that was actually typed.
    if (doctorEmail.trim() && !isValidEmail(doctorEmail)) {
      await showAlert('Recipient Email is not a valid email address', 'Validation');
      return;
    }
    const invalidCc = findInvalidEmails(ccEmail);
    if (invalidCc.length > 0) {
      await showAlert(`Invalid CC email: ${invalidCc.join(', ')}`, 'Validation');
      return;
    }
    const invalidBcc = findInvalidEmails(bccEmail);
    if (invalidBcc.length > 0) {
      await showAlert(`Invalid BCC email: ${invalidBcc.join(', ')}`, 'Validation');
      return;
    }
    // `min` only constrains the picker; a typed or pasted date still gets through,
    // and a past date would otherwise be clamped to the 1-hour floor below and
    // produce a link that expires almost immediately.
    if (!neverExpires && expirationDate && expirationDate < hkDateISO()) {
      await showAlert('Link Expiry Date cannot be in the past', 'Validation');
      return;
    }
    setGenerating(true);
    try {
      let expirationHours = null;
      if (!neverExpires && expirationDate) {
        const now = new Date();
        const selected = new Date(`${expirationDate}T23:59:59`);
        const diffMs = selected.getTime() - now.getTime();
        expirationHours = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60)));
      }

      const response = await api.post('/formlinks', {
        formId,
        patientName: patientName || null,
        notes: notes || null,
        expirationHours,
        reusable,
        doctorEmail: doctorEmail || null,
        ccEmail: ccEmail || null,
        bccEmail: bccEmail || null
      });
      // Build the link URL using the production domain
      const data = response.data;
      if (data.token) {
        const baseUrl = window.location.origin;
        data.url = `${baseUrl}/fill/${data.token}`;
      }
      setLinkResult(data);
    } catch (error) {
      await showAlert('Error generating link: ' + (error.response?.data?.error || error.message), 'Error');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    const url = linkResult?.url;
    if (!url) return;

    const copied = await copyTextToClipboard(url);
    if (copied) {
      showAlert('Link copied to clipboard!', 'Success');
      return;
    }

    // Genuinely could not copy — select the field so the link is one keystroke away
    // instead of leaving the user with a success message and an empty clipboard.
    const input = document.getElementById('generatedLinkUrl');
    if (input) input.select();
    showAlert('Please press Ctrl+C to copy the selected link', 'Info');
  };

  const handleApplyCustomHost = () => {
    if (!customHost || !linkResult?.url) return;
    try {
      const url = new URL(linkResult.url);
      url.host = customHost;
      setLinkResult({ ...linkResult, url: url.toString() });
    } catch {
      showAlert('Invalid URL format', 'Error');
    }
  };

  const handleGenerateAnother = () => {
    setPatientName('');
    setNotes('');
    setLinkResult(null);
  };

  // Backdrop clicks are deliberately inert: closing there loses a half-filled form,
  // or a link that was just generated. Close with the X, Cancel or Close instead.
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>Generate Form Link</h3>
          <button className="modal-close-btn" onClick={onClose}>X</button>
        </div>

        {!linkResult ? (
          <div className="modal-body">
            <div className="form-group">
              <label>Recipient Email</label>
              <input
                type="email"
                className="form-control"
                value={doctorEmail}
                onChange={(e) => setDoctorEmail(e.target.value)}
                placeholder="Enter recipient email"
                list="doctor-email-suggestions"
              />
              <datalist id="doctor-email-suggestions">
                {doctors.map(d => (
                  <option key={d.id} value={d.email}>{d.name}</option>
                ))}
              </datalist>
            </div>
            <div className="form-group">
              <label>CC Email (optional)</label>
              <input type="email" multiple className="form-control" value={ccEmail} onChange={(e) => setCcEmail(e.target.value)} placeholder="cc@example.com" />
              <small style={{ color: '#6b7280' }}>Visible to everyone on the notification. Separate multiple with a comma.</small>
            </div>
            <div className="form-group">
              <label>BCC Email (optional)</label>
              <input type="email" multiple className="form-control" value={bccEmail} onChange={(e) => setBccEmail(e.target.value)} placeholder="bcc@example.com" />
              <small style={{ color: '#6b7280' }}>Hidden from other recipients. Separate multiple with a comma.</small>
            </div>
            <div className="form-group">
              <label>Notes (optional)</label>
              <textarea className="form-control" value={notes} onChange={(e) => setNotes(e.target.value)} rows="2" placeholder="Any notes..." />
            </div>
            <div className="form-group">
              <label>Link Expiration</label>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <input
                  type="date"
                  className="form-control"
                  value={expirationDate}
                  min={hkDateISO()}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  disabled={neverExpires}
                  style={{ flex: 1, opacity: neverExpires ? 0.6 : 1 }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={neverExpires}
                    onChange={(e) => setNeverExpires(e.target.checked)}
                  />
                  Never expires
                </label>
              </div>
            </div>
            <div className="form-group">
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.9rem 1rem',
                  border: reusable ? '1.5px solid var(--company-primary-color, #273947)' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: reusable ? '#e8f1f4' : '#f9fafb',
                  color: '#111827'
                }}
              >
                <input type="checkbox" checked={reusable} onChange={(e) => setReusable(e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--company-primary-color, #273947)' }} />
                <span>
                  <strong style={{ display: 'block', marginBottom: '0.15rem' }}>Reusable link</strong>
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Can be submitted multiple times</span>
                </span>
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleCreateLink} disabled={generating}>
                {generating ? 'Generating...' : 'Generate Link'}
              </button>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="modal-body">
            <div className="form-group">
              <label>Generated Link</label>
              <input type="text" id="generatedLinkUrl" className="form-control" value={linkResult.url} readOnly style={{ background: '#f5f5f5' }} />
            </div>
            {linkResult.expiresAt && (
              <p style={{ fontSize: '0.85rem', color: '#666' }}>
                Expires: {new Date(linkResult.expiresAt).toLocaleString()}
              </p>
            )}
            {!linkResult.expiresAt && (
              <p style={{ fontSize: '0.85rem', color: '#666' }}>This link never expires.</p>
            )}
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleCopyLink}>Copy Link</button>
              <button className="btn btn-secondary" onClick={handleGenerateAnother}>Generate Another</button>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
